import { CloudUpload } from '@mui/icons-material';
import { Box, Button, Checkbox, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { FormikErrors, useFormik } from 'formik';
import * as Yup from 'yup';
import { RootState } from '../../../../../redux/store';
import { CreateMileStone } from '../../../../../utils/services/project/project';
import { showSnackbar } from '../../../../../redux/reducer/snackbarSlice';
import { setModuleRefresh } from '../../../../../redux/reducer/registration/RefreshSlice';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PercentIcon from '@mui/icons-material/Percent';
import dayjs from 'dayjs';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import FlagIcon from '@mui/icons-material/Flag';
import { closeDrawer } from '../../../../../redux/reducer/drawerSlice';
import { AmountDisplay } from '../../../../../utils/utils';
import PageLang from '../../../../../config/page.languge';

const ProjectMilestonesForm = () => {
    const [loading, setLoading] = useState(false);
    const drawerPayload = useSelector((state: RootState) => state.drawer.drawerData)
    const lang = useSelector((state: RootState) => state.lang.language)

    const Achiveoptions = [
        { label: "Amount", value: "amount" },
        { label: "Percentage", value: "percentage" },
    ];
    const dispatch = useDispatch();

    const formik = useFormik({
        initialValues: {
            type: "percentage",
            milestones: [
                {
                    name: '',
                    description: '',
                    percentage: null,
                    amount: null,
                    completionDate: null,
                    completed: false,
                    file: null as File | null,
                }
            ],
        },
        validationSchema: Yup.object({
            type: Yup.string().required("Type is required"),
            milestones: Yup.array().of(
                Yup.object().shape({
                    name: Yup.string().required('Milestone name is required'),
                    description: Yup.string().required('Description is required'),
                    percentage: Yup.number().when('type', {
                        is: 'percentage',
                        then: (schema) => schema.required('Percentage is required'),
                        otherwise: (schema) => schema.notRequired()
                    }),
                    amount: Yup.number().when('type', {
                        is: 'amount',
                        then: (schema) => schema.required('Amount is required'),
                        otherwise: (schema) => schema.notRequired()
                    }),
                    completionDate: Yup.date()
                        .required('Completion date is required'),
                    file: Yup.mixed()
                        .required('Please upload a document')
                        .test('fileType', 'Only JPG, JPEG, PNG, or PDF files are allowed', (value) => {
                            if (!value) return false;
                            const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
                            return acceptedTypes.includes((value as File).type);
                        })
                })
            )
        }),
        onSubmit: async (values, { setSubmitting }) => {
            setLoading(true);
            const payload = new FormData();
            payload.append(`project_id`, drawerPayload);
            payload.append(`type`, values.type);
            values.milestones.forEach((milestone, index) => {
                payload.append(`milestones[${index}][name]`, milestone.name);
                payload.append(`milestones[${index}][description]`, milestone.description);
                milestone.percentage && payload.append(`milestones[${index}][percentage]`, String(milestone.percentage));
                milestone.amount && payload.append(`milestones[${index}][amount]`, String(milestone.amount));
                payload.append(`milestones[${index}][completionDate]`, String(dayjs(milestone.completionDate).format("YYYY-MM-DD")));
                payload.append(`milestones[${index}][completed]`, String(milestone.completed));
                if (milestone.file) {
                    payload.append(`milestones[${index}][file]`, milestone.file);
                }
            });

            const { code, data, message } = await CreateMileStone(payload);
            if (code === 200) {
                dispatch(showSnackbar({ type: "success", message: message || 'Milestone Created Succesfully.' }));

                dispatch(closeDrawer());
                dispatch(setModuleRefresh({ moduleName: 'milestone_list', refresh: true }));
            }
            else {
                dispatch(showSnackbar({ type: "error", message: message || 'Something Went Wrong!' }));
            }

            setLoading(false);
            setSubmitting(false);
        }
    });

    const addMilestone = () => {
        formik.setFieldValue("milestones", [
            ...formik.values.milestones,
            {
                name: '',
                description: '',
                percentage: 0,
                amount: 0,
                completionDate: null,
                completed: false,
                file: null as File | null,
            }
        ]);
    };

    const removeMilestone = (index: number) => {
        const updatedMilestones = [...formik.values.milestones];
        updatedMilestones.splice(index, 1);
        formik.setFieldValue("milestones", updatedMilestones);
    };


    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        if (event.target.files && event.target.files.length > 0) {
            formik.setFieldValue(`milestones[${index}].file`, event.target.files[0]);
        }
    };

    return (
        <Box sx={{ width: "100%", p: 2, maxHeight: "90vh", overflowX: "auto", overflowY: "auto" }}>
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                    <Grid container alignItems="center" sx={{ mt: 1 }} justifyContent="space-between">
                        <Grid item>
                            <Button variant="outlined" sx={{ ml: 3 }} color="primary" onClick={addMilestone} startIcon={<AddCircleOutlineIcon />}>
                                Milestone
                            </Button>
                        </Grid>
                        <Grid item>
                            <FormControl component="fieldset" >
                                <RadioGroup
                                    row
                                    name="milestones.type"
                                    value={formik.values.type}
                                    onChange={(event) => formik.setFieldValue("type", event.target.value)}
                                    onBlur={() => formik.setFieldTouched("type", true)}
                                >
                                    {Achiveoptions.map((option) => (
                                        <FormControlLabel
                                            key={option.value}
                                            value={option.value}
                                            control={<Radio />}
                                            label={option.label}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {formik.values.milestones?.map((milestone, index) => (
                        <Grid item xs={12} key={index}>
                            <Box sx={{ border: '2px solid #ccc', p: 1.5, mb: 2, borderRadius: 1 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} >
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ backgroundColor: '#ffcdcd', px: 1, borderRadius: 1 }}>
                                            <Typography variant="h6"><b>Milestone {index + 1}</b></Typography>
                                            <IconButton
                                                color="primary"
                                                disabled={formik.values.milestones?.length === 1}
                                                onClick={() => removeMilestone(index)}
                                            >
                                                <RemoveCircleOutlineIcon />
                                            </IconButton>
                                        </Stack>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Milestone Name *"
                                            name={`milestones[${index}].name`}
                                            value={milestone.name}
                                            onChange={formik.handleChange}
                                            error={
                                                formik.touched.milestones?.[index]?.name &&
                                                Boolean(
                                                    (formik.errors.milestones?.[index] as FormikErrors<typeof formik.values.milestones[number]>)?.name
                                                )
                                            }
                                            helperText={
                                                formik.touched.milestones?.[index]?.name &&
                                                (formik.errors.milestones?.[index] as FormikErrors<typeof formik.values.milestones[number]>)?.name
                                            }
                                            size="small"
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3.5} md={3.5}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                sx={{
                                                    "& .MuiInputBase-root": { alignItems: "center" },
                                                    "& .MuiInputAdornment-root": { marginRight: "10px" },
                                                }}
                                                label="Completion Date *"
                                                format='DD-MMM-YYYY'
                                                maxDate={dayjs().add(365, "day")}
                                                minDate={dayjs()}
                                                value={milestone.completionDate}
                                                onChange={(newValue) => formik.setFieldValue(`milestones[${index}].completionDate`, newValue)}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        fullWidth: true,
                                                        error: formik.touched.milestones?.[index]?.completionDate && Boolean(
                                                            (formik.errors.milestones?.[index] as FormikErrors<typeof formik.values.milestones[number]>)?.completionDate
                                                        ),
                                                        helperText: formik.touched.milestones?.[index]?.completionDate && (formik.errors.milestones?.[index] as FormikErrors<typeof formik.values.milestones[number]>)?.completionDate
                                                        ,
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>

                                    <Grid item xs={12} sm={2.5} md={2.5}>
                                        {formik.values.type === "percentage" ? (
                                            <TextField
                                                fullWidth
                                                label="Percentage *"
                                                name={`milestones[${index}].percentage`}
                                                value={milestone.percentage}
                                                onChange={(e) => {
                                                    let value = e.target.value.replace(/[^0-9]/g, "");
                                                    if (value !== "" && (parseInt(value) < 0 || parseInt(value) > 100)) return;
                                                    formik.setFieldValue(`milestones[${index}].percentage`, value);
                                                }}
                                                focused
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end" sx={{ mr: 1 }}>
                                                            <PercentIcon />
                                                        </InputAdornment>
                                                    ),
                                                    sx: { textAlign: "right", input: { textAlign: "right" } },
                                                }}
                                                size="small"
                                            />
                                        )
                                            : (
                                                <TextField
                                                    fullWidth
                                                    label="Amount *"
                                                    name={`milestones[${index}].amount`}
                                                    value={AmountDisplay(milestone.amount || 0)}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/[^0-9]/g, "");
                                                        formik.setFieldValue(`milestones[${index}].amount`, value);
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start" sx={{ ml: 1, }}>
                                                                <CurrencyRupeeIcon />
                                                            </InputAdornment>
                                                        ),
                                                        sx: { textAlign: "right", input: { textAlign: "right" } },
                                                    }}
                                                    inputProps={{ maxLength: 10, }}
                                                    size="small"
                                                />
                                            )}
                                    </Grid>

                                    <Grid item xs={12} sm={12}>
                                        <TextField
                                            fullWidth
                                            label="Description *"
                                            name={`milestones[${index}].description`}
                                            value={milestone.description}
                                            onChange={formik.handleChange}
                                            error={
                                                formik.touched.milestones?.[index]?.description &&
                                                Boolean(
                                                    (formik.errors.milestones?.[index] as FormikErrors<typeof formik.values.milestones[number]>)?.description
                                                )
                                            }
                                            helperText={
                                                formik.touched.milestones?.[index]?.description &&
                                                (formik.errors.milestones?.[index] as FormikErrors<typeof formik.values.milestones[number]>)?.description
                                            }
                                            size="small"
                                            multiline
                                            rows={2}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={12} md={12}>
                                        <Button
                                            sx={{ width: "100%", height: 50, backgroundColor: "grey.100" }}
                                            size="small"
                                            component="label"
                                            variant="outlined"
                                            color="info"
                                            startIcon={<CloudUpload fontSize="large" />}
                                        >
                                            {formik.values.milestones[index]?.file ? (
                                                <small
                                                    style={{
                                                        textOverflow: "ellipsis",
                                                        overflow: "hidden",
                                                        whiteSpace: "nowrap",
                                                        maxWidth: "85%",
                                                    }}
                                                >
                                                    {formik.values.milestones[index]?.file?.name}
                                                </small>
                                            ) : (
                                                "Upload file *"
                                            )}
                                            <input
                                                type="file"
                                                hidden
                                                name={`milestones[${index}].file`}
                                                onChange={(e) => handleFileChange(e, index)}
                                                accept=".jpg,.jpeg,.png,.pdf"
                                            />
                                        </Button>
                                        {formik.touched.milestones?.[index]?.file &&
                                            (formik.errors.milestones?.[index] as FormikErrors<typeof formik.values.milestones[number]>)?.file && (
                                                <Typography color="error" variant="caption">
                                                    {(formik.errors.milestones?.[index] as FormikErrors<typeof formik.values.milestones[number]>)?.file}
                                                </Typography>
                                            )}
                                    </Grid>
                                    <Grid item xs={12} sm={12} display="flex" alignItems="center" >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={milestone.completed}
                                                    onChange={(e) => formik.setFieldValue(`milestones[${index}].completed`, e.target.checked)}
                                                />
                                            }
                                            label="Mark As Check. ( If Milestone is Completed ? )"
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    ))}
                    <Grid item xs={12} sx={{ textAlign: 'center' }}>
                        <Button variant='contained' type='submit' startIcon={<FlagIcon />}>
                            {PageLang?.projectCreationFormData.addMilestones[lang]}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Box>
    )
}
export default ProjectMilestonesForm;